import { useState, useMemo, useEffect } from 'react';

export interface UseDataTableOptions<T> {
  data: T[];
  searchFields?: (keyof T | string)[];
  initialSortBy?: keyof T | string;
  initialSortDir?: 'asc' | 'desc';
  initialRowsPerPage?: number;
  customSearchFn?: (item: T, query: string) => boolean;
}

export function useDataTable<T>({
  data,
  searchFields = [],
  initialSortBy = '',
  initialSortDir = 'asc',
  initialRowsPerPage = 10,
  customSearchFn,
}: UseDataTableOptions<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<keyof T | string>(initialSortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Reset page to 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSort = (column: keyof T | string) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  const processedData = useMemo(() => {
    let result = [...data];

    // 1. Search
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter((item) => {
        if (customSearchFn) {
          return customSearchFn(item, query);
        }
        // Default search logic across specified fields
        return searchFields.some((field) => {
          const val = (item as any)[field];
          if (val === null || val === undefined) return false;
          return String(val).toLowerCase().includes(query);
        });
      });
    }

    // 2. Sort
    if (sortBy) {
      result.sort((a, b) => {
        const valA = (a as any)[sortBy];
        const valB = (b as any)[sortBy];

        if (valA === valB) return 0;
        
        // Handle undefined/null values safely
        if (valA == null) return sortDir === 'asc' ? 1 : -1;
        if (valB == null) return sortDir === 'asc' ? -1 : 1;

        const comparison = valA > valB ? 1 : -1;
        return sortDir === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchQuery, searchFields, sortBy, sortDir, customSearchFn]);

  const totalRows = processedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  // Ensure current page is valid after filtering/deleting
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedData = useMemo(() => {
    const startIdx = (safeCurrentPage - 1) * rowsPerPage;
    return processedData.slice(startIdx, startIdx + rowsPerPage);
  }, [processedData, safeCurrentPage, rowsPerPage]);

  return {
    searchQuery,
    setSearchQuery,
    sortBy,
    sortDir,
    handleSort,
    currentPage: safeCurrentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalRows,
    totalPages,
    paginatedData,
    processedData,
  };
}
